import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Button, ImageWithPlaceholder, Typography } from '@wbk/ui';
import { Link } from '@wbk/config';
import { type Zone } from '@wbk/contentful/api';
import ImageOrVideo from '@/components/ui/ImageOrVideo';
import HeroTitle from '@/components/ui/HeroTitle';
import { getWebookUrl, isExternalUrl } from '@/utils/redirect';
import useLocalization from '@/context/localization/useLocalization';

type Props = {
  section?: Zone;
};

const HeroSection = ({ section }: Props) => {
  const { t } = useTranslation('common');
  const { locale } = useLocalization();
  const banner = section?.banner || section?.featuredBanner;

  const webookUrl = getWebookUrl(locale);
  const redirectPathname = section?.button?.link;

  const externalLink = redirectPathname
    ? isExternalUrl(redirectPathname)
      ? redirectPathname
      : `${webookUrl}/zones/${redirectPathname}`
    : null;

  const hasSponsor = !!section?.sponsorLogo?.url;

  return (
    <>
      <div className='relative h-dvh'>
        <div className='absolute inset-0'>
          <div className='pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/80 mix-blend-multiply' />

          <ImageOrVideo
            className='h-full w-full object-cover'
            src={banner?.url}
            type={banner?.contentType?.includes('video') ? 'video' : 'image'}
          />
        </div>
        <div className='container-padding container h-full w-full'>
          <div className='container relative h-full w-full px-3 xl:px-0'>
            <div className='absolute inset-x-0 bottom-4 flex flex-col justify-between gap-4 md:bottom-20'>
              <div className='flex items-center gap-6'>
                <div
                  className={twMerge(
                    hasSponsor && 'border-white/20 ltr:border-r ltr:pr-6 rtl:border-l rtl:pl-6'
                  )}
                >
                  <ImageWithPlaceholder
                    src={section?.zoneLogo?.url}
                    alt={section?.zoneLogo?.title || section?.title}
                    className='size-14 object-contain md:size-24'
                  />
                </div>

                {hasSponsor && (
                  <div className='flex flex-col gap-1'>
                    <Typography as='p' variant='body-XXS'>
                      {t('common:presented_by')}
                    </Typography>
                    <ImageWithPlaceholder
                      src={section?.sponsorLogo?.url}
                      alt={section?.sponsorLogo?.title || section?.title}
                      className='size-14 object-contain md:size-24'
                    />
                  </div>
                )}
              </div>
              <div className='flex flex-col justify-between gap-4 md:flex-row md:items-end'>
                <div className='flex max-w-xs flex-col gap-2 md:max-w-lg rtl:gap-8'>
                  <Typography as='p' variant='body-M' className='text-body-L font-semibold'>
                    {section?.subtitle}
                  </Typography>

                  <HeroTitle title={section?.title || ''} className='uppercase' />
                </div>

                <div className='flex flex-col items-end gap-4'>
                  <div className='flex max-w-xl flex-col justify-between text-[#A1A1AA]'>
                    <Typography
                      as='p'
                      variant='body-S'
                      className='xl:text-body-M 2xl:text-body-L line-clamp-3'
                    >
                      {section?.description}
                    </Typography>
                  </div>
                  {externalLink && (
                    <Button
                      as={Link}
                      to={externalLink}
                      target='_blank'
                      className='bg-primary-100 hover:bg-primary-100/90 flex w-full items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors sm:text-[15px] md:w-auto'
                    >
                      {t('get_ticket')}
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 21 21'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          opacity='0.3'
                          d='M9.58313 2.1665C10.0434 2.1665 10.4165 2.5396 10.4165 2.99983C10.4165 3.46007 10.0434 3.83316 9.58313 3.83316H5.49982C4.57935 3.83316 3.83316 4.57935 3.83316 5.49982V15.4998C3.83316 16.4202 4.57935 17.1664 5.49982 17.1664H15.4998C16.4202 17.1664 17.1664 16.4202 17.1664 15.4998V13.8331C17.1664 13.3729 17.5395 12.9998 17.9998 12.9998C18.46 12.9998 18.8331 13.3729 18.8331 13.8331V15.4998C18.8331 17.3407 17.3407 18.8331 15.4998 18.8331H5.49982C3.65888 18.8331 2.1665 17.3407 2.1665 15.4998V5.49982C2.1665 3.65888 3.65888 2.1665 5.49982 2.1665H9.58313Z'
                          fill='#F4F4F5'
                        />
                        <path
                          d='M6.33725 12.081C6.33725 11.4454 6.20105 8.93705 8.17972 6.94703C9.51144 5.60775 11.5045 5.37545 13.888 5.26952V2.31368C13.888 1.66294 14.6599 1.32097 15.1419 1.75821L20.2553 6.39726C20.5811 6.69284 20.5838 7.20408 20.261 7.503L15.1476 12.2386C14.6674 12.6832 13.888 12.3427 13.888 11.6883V8.75621C12.3746 8.79782 11.2967 8.89408 10.3173 9.20566C8.42432 9.80788 7.30106 12.3902 6.99815 13.1768C6.94592 13.3125 6.81654 13.4051 6.67118 13.4051C6.48467 13.4051 6.33353 13.254 6.33411 13.0675C6.33519 12.7241 6.33725 12.3294 6.33725 12.081Z'
                          fill='white'
                        />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
